/**
 * ConsumptionChart_V2 - Actor Device Chart (Water Heater, Boiler, etc.)
 *
 * Displays 15-minute interval data for actor/consumption devices with:
 * - Consumption bars
 * - Output state line (0/1)
 * - Override state line
 * - PV output line (if available)
 * - Zero reference line
 * - Dual Y-axis (consumption left, state/output right)
 */

import React, { useMemo, useRef, useEffect, useState } from 'react';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer
} from 'recharts';
import { InfoBox } from '../../Components';
import { formatKWh, formatEuros, formatCents } from '../ChartUtils';
import {
  extractTimeseries,
  extractDeviceData,
  aggregateDeviceDataToHourly,
  getHourlyTicks,
  formatHourLabel,
  getResponsiveBarSize,
  isMobileLayout
} from './heatingChartUtils';
import { t } from '../../i18n';

const ConsumptionChart_V2 = ({ data, actorId, metadata, isMobile: isMobileProp = null, showDetails = false }) => {
  console.log('ConsumptionChart_V2: Rendering actor device', actorId);

  const chartRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  // Extract timeseries and device data
  const timeseries = useMemo(() => extractTimeseries(data), [data]);
  const deviceData = useMemo(() =>
    extractDeviceData(timeseries, actorId),
    [timeseries, actorId]
  );

  // Device details from metadata
  const deviceDetails = metadata.actor_details?.[actorId];

  // Determine if mobile layout (use prop if provided, otherwise detect)
  const isMobile = useMemo(() => {
    return isMobileProp !== null ? isMobileProp : isMobileLayout(containerWidth);
  }, [isMobileProp, containerWidth]);

  // Process chart data (15-min or hourly based on mobile + toggle)
  const chartData = useMemo(() => {
    const shouldAggregate = isMobile && !showDetails;
    const source = shouldAggregate ? aggregateDeviceDataToHourly(deviceData) : deviceData;

    return source.map(snapshot => {
      // Check if PV output exists and is non-zero
      const hasPvOutput = snapshot.pv_output !== undefined && snapshot.pv_output !== 0;

      return {
        timestamp: snapshot.timestamp,
        consumption: snapshot.consumption || 0,
        output: hasPvOutput ? 0 : (snapshot.output_state || 0), // Hide regular output if PV is active
        override: snapshot.override || 0,
        pv_output: snapshot.pv_output || 0,
        zeroLine: 0 // Reference line at zero
      };
    });
  }, [deviceData, isMobile, showDetails]);

  // Get hourly ticks
  const hourlyTicks = useMemo(() => {
    if (isMobile && !showDetails) {
      // Show fewer ticks on mobile hourly view
      return chartData
        .filter((_, index) => index % 2 === 0) // Every 2 hours
        .map(d => d.timestamp);
    }
    return getHourlyTicks(chartData);
  }, [chartData, isMobile, showDetails]);

  // Bar size based on container width
  const barSize = useMemo(() => {
    return getResponsiveBarSize(containerWidth);
  }, [containerWidth]);

  // Update container width
  useEffect(() => {
    const updateWidth = () => {
      if (chartRef.current) {
        const wrapper = chartRef.current.querySelector('.recharts-wrapper');
        if (wrapper) {
          setContainerWidth(wrapper.getBoundingClientRect().width);
        }
      }
    };

    const timer = setTimeout(updateWidth, 100);
    window.addEventListener('resize', updateWidth);

    return () => {
      window.removeEventListener('resize', updateWidth);
      clearTimeout(timer);
    };
  }, []);

  // Early return after all hooks
  if (!deviceDetails) {
    console.warn('No actor device details found for', actorId);
    return null;
  }

  // Check if PV output exists in the data
  const hasPvOutput = chartData.some(d => d.pv_output > 0);

  // InfoBox data
  const actorInfo = [
    { label: t('heatingChart.totalConsumption'), value: formatKWh(deviceDetails.total_consumption) },
    { label: t('heatingChart.totalCost'), value: formatCents(deviceDetails.total_cost ) },
    { label: t('heatingChart.configProfile'), value: deviceDetails.profile || 'N/A' }
  ];

  // Validation
  if (chartData.length === 0) {
    console.warn('ConsumptionChart_V2: No chart data for actor device', actorId);
    return (
      <div>
        <InfoBox title={deviceDetails.name} dataPoints={actorInfo} />
        <p style={{ textAlign: 'center', color: '#888', padding: '20px' }}>
          No data available for this device
        </p>
      </div>
    );
  }

  return (
    <div>
      <InfoBox title={deviceDetails.name} dataPoints={actorInfo} />

      <div ref={chartRef}>
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart
            data={chartData}
            margin={{ top: 20, right: 30, bottom: 20, left: 20 }}
          >
            <CartesianGrid stroke="#f5f5f5" />

            <XAxis
              dataKey="timestamp"
              tickFormatter={formatHourLabel}
              ticks={hourlyTicks}
              interval={0}
              tick={{ fontSize: 12 }}
            />

            <YAxis
              yAxisId="left"
              orientation="left"
              label={{ value: t('heatingChart.consumption') + ' (kWh)', angle: -90, position: 'insideLeft' }}
              domain={['auto', 'auto']}
              stroke="#413ea0"
            />

            <YAxis
              yAxisId="right"
              orientation="right"
              label={{ value: t('heatingChart.output'), angle: 90, position: 'insideRight' }}
              domain={['auto', 'auto']}
              stroke="#8884d8"
            />

            <Tooltip
              content={({ active, payload, label }) => {
                if (active && payload && payload.length) {
                  // Filter out Zero Line from tooltip
                  const filteredPayload = payload.filter(entry => entry.dataKey !== 'zeroLine');

                  if (filteredPayload.length > 0) {
                    return (
                      <div className="custom-tooltip" style={{ backgroundColor: '#fff', padding: '10px', border: '1px solid #ccc' }}>
                        <p className="label">{`Time: ${formatHourLabel(label)}`}</p>
                        {filteredPayload.map((entry, index) => (
                          <p key={`item-${index}`} style={{ color: entry.color }}>
                            {`${entry.name}: ${entry.name.includes('Consumption') ? formatKWh(entry.value) : entry.value}`}
                          </p>
                        ))}
                      </div>
                    );
                  }
                }
                return null;
              }}
            />

            <Legend />

            {/* Consumption Bar */}
            <Bar
              yAxisId="left"
              dataKey="consumption"
              name={t('heatingChart.hourlyConsumption')}
              fill="#8884d8"
              barSize={barSize}
            />

            {/* Output State Line (green) */}
            <Line
              yAxisId="right"
              type="stepAfter"
              dataKey="output"
              name={t('heatingChart.output')}
              stroke="#82ca9d"
              strokeWidth={2}
              dot={false}
            />

            {/* Override State Line (orange) */}
            <Line
              yAxisId="right"
              type="stepAfter"
              dataKey="override"
              name={t('heatingChart.override')}
              stroke="#FFA500"
              strokeWidth={2}
              dot={false}
            />

            {/* PV Output Line (dark yellow) - only if exists */}
            {hasPvOutput && (
              <Line
                yAxisId="right"
                type="stepAfter"
                dataKey="pv_output"
                name={t('heatingChart.pvOutput')}
                stroke="#B8860B"
                strokeWidth={2}
                dot={false}
              />
            )}

            {/* Zero Reference Line */}
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="zeroLine"
              name="Zero Line"
              stroke="#666"
              strokeWidth={2}
              dot={false}
              legendType="none"
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <p></p>
    </div>
  );
};

export default ConsumptionChart_V2;
